//@ts-nocheck
import React, { useEffect, useState } from "react";
import { Button, Checkbox, Input, message, Modal, Spin } from "antd";
import {
  CheckCircleFilled,
  WarningFilled,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  SendOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  SwapOutlined,
  ShoppingOutlined,
  ProfileOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleFilled,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import {
  getVendorContext,
  submitVendorResponse,
  getCompaniesPublic,
  type VendorResponseItem,
} from "../../utils/API";

// Public, standalone vendor-response form opened from a tokenized link inside a
// WTB email. No Master layout / CommonHeader / navigation — external vendors are
// not logged in. Brand / Model are restricted to the equipment that was offered
// in the email; each item row becomes its own pending record on our platform.

type Item = {
  key: string;
  eqBrand: string;
  eqModel: string;
  options: string;
  comment: string;
  price: string;
  askingPrice: string;
  qty: string;
};

type EmailItem = { brand?: string; model?: string; options?: string };

const BRAND = "#004AAD"; // Alliance Test brand blue
const BRAND_DARK = "#003a8c";
const BRAND_RGB = "0,74,173";

const blankItem = (over: Partial<Item> = {}): Item => ({
  key: `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  eqBrand: "",
  eqModel: "",
  options: "",
  comment: "",
  price: "",
  askingPrice: "",
  qty: "",
  ...over,
});

// Format a value as US currency (1,234.56). Leaves blank/invalid values as-is.
const formatUsd = (value: string) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const n = Number(raw.replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const VendorQuoteResponse: React.FC = () => {
  const { token: tokenParam } = useParams();
  const token = String(tokenParam || "").trim();

  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const VENDOR_DETAILS_KEY = "vendorQuoteResponseDetails";

  const [emailItems, setEmailItems] = useState<EmailItem[]>([]);
  const [common, setCommon] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(VENDOR_DETAILS_KEY) || "null");
      return {
        companyName: saved?.companyName || "",
        email: saved?.email || "",
      };
    } catch {
      return { companyName: "", email: "" };
    }
  });
  // Default checked: the vendor gets an emailed copy of their submission unless
  // they opt out before submitting.
  const [sendCopy, setSendCopy] = useState(true);
  const [items, setItems] = useState<Item[]>([blankItem()]);

  // Company-name matching against our existing companies list. This is purely
  // a frontend convenience for the vendor's own form field — it never touches
  // the backend/submission payload. Only checked once the vendor has finished
  // typing the full name (on blur), not on every keystroke:
  //  - exactly one company matches the typed name -> silently replace the
  //    input with our stored (canonical) spelling/casing.
  //  - more than one company matches -> show a pick-one suggestion list.
  //  - no match -> show nothing.
  const [companySuggestions, setCompanySuggestions] = useState<string[]>([]);

  // On-open check: if a company name was already saved in localStorage from a
  // previous visit, quietly verify it against our records as soon as the form
  // loads. This is separate from the on-blur check above — it only runs once,
  // right when the form opens, and only when there's a saved name to verify.
  //  - exact (case-insensitive) match -> everything lines up, say nothing.
  //  - exactly one close match -> politely ask the vendor to confirm it.
  //  - multiple close matches -> politely ask the vendor to pick the right one.
  //  - no match at all -> say nothing; it may simply be a new company.
  const [savedNameModal, setSavedNameModal] = useState<{
    open: boolean;
    typedName: string;
    matches: string[];
  }>({ open: false, typedName: "", matches: [] });

  const checkSavedCompanyNameOnOpen = async (typed: string) => {
    const name = typed.trim();
    if (!name) return;
    if (!token) return;
    try {
      const res = await getCompaniesPublic(token, { search: name, limit: 10 });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const typedKey = name.toLowerCase();

      const exact = rows.find(
        (c: any) => String(c?.name || "").trim().toLowerCase() === typedKey,
      );
      // Already a perfect match on record — nothing to bother the vendor with.
      if (exact) return;

      if (rows.length >= 1) {
        setSavedNameModal({
          open: true,
          typedName: name,
          matches: rows.map((c: any) => c.name).filter(Boolean),
        });
      }
    } catch {
      // Non-blocking: if the lookup fails, just leave things as they are.
    }
  };

  const handleConfirmSavedName = (name: string) => {
    setCommon((p) => ({ ...p, companyName: name }));
    setSavedNameModal({ open: false, typedName: "", matches: [] });
  };

  const handleKeepTypedName = () => {
    setSavedNameModal({ open: false, typedName: "", matches: [] });
  };

  const checkCompanyNameMatch = async () => {
    const typed = common.companyName.trim();
    setCompanySuggestions([]);
    if (!typed) return;

    if (!token) return;
    try {
      const res = await getCompaniesPublic(token, { search: typed, limit: 10 });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const typedKey = typed.toLowerCase();

      // Prefer an exact (whole-name, case-insensitive) match if one exists —
      // that's the vendor's own company, so replace silently with no prompt.
      const exact = rows.find(
        (c: any) => String(c?.name || "").trim().toLowerCase() === typedKey,
      );
      if (exact) {
        setCommon((p) => ({ ...p, companyName: exact.name }));
        return;
      }

      // Otherwise fall back to the server's substring matches (e.g. vendor
      // typed "Global Test" and we have "Global Test Equipment" on file).
      if (rows.length === 1) {
        setCommon((p) => ({ ...p, companyName: rows[0].name }));
      } else if (rows.length > 1) {
        setCompanySuggestions(rows.map((c: any) => c.name));
      }
    } catch {
      // Non-blocking: if the lookup fails, just leave the vendor's typed text as-is.
    }
  };

  // Runs once, on initial mount, so the vendor sees this right when the form
  // opens rather than after they start typing.
  useEffect(() => {
    if (common.companyName.trim()) {
      checkSavedCompanyNameOnOpen(common.companyName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) {
      setTokenError("Missing token. Please use the link from your email.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const ctx = await getVendorContext(token);
        const rawList: EmailItem[] = Array.isArray(ctx.items) ? ctx.items : [];
        // Collapse duplicates — the same brand + model should appear only once.
        const seen = new Set<string>();
        const list = rawList.filter((e) => {
          const dedupeKey = `${(e.brand || "").trim().toLowerCase()}|${(e.model || "")
            .trim()
            .toLowerCase()}`;
          if (seen.has(dedupeKey)) return false;
          seen.add(dedupeKey);
          return true;
        });
        setEmailItems(list);
        // Pre-load a row for every brand/model from the email. Brand & Model are
        // fixed from the want list — the vendor only fills in the other fields.
        setItems(
          list.length
            ? list.map((e) =>
                blankItem({
                  eqBrand: e.brand || "",
                  eqModel: e.model || "",
                  options: e.options || "",
                }),
              )
            : [blankItem()],
        );
        if (ctx.vendorEmail) setCommon((p) => (p.email ? p : { ...p, email: ctx.vendorEmail }));
      } catch (error: any) {
        const status = error?.response?.status;
        setTokenError(
          error?.response?.data?.error ||
            (status === 410
              ? "This link has expired. Please request a new one."
              : "Invalid or expired link."),
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const updateItem = (key: string, patch: Partial<Item>) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));

  const handleSubmit = async () => {
    if (!common.companyName.trim()) {
      message.warning("Company Name is required.");
      return;
    }
    if (!common.email.trim()) {
      message.warning("Email is required.");
      return;
    }
    // Only rows the vendor actually priced are submitted — brand/model are
    // pre-filled on every row, so the entered price is what signals an offer.
    const validItems = items.filter(
      (it) => it.eqBrand.trim() && it.eqModel.trim() && it.price.trim(),
    );
    if (!validItems.length) {
      message.warning("Enter a price for at least one item before submitting.");
      return;
    }
    try {
      setSubmitting(true);
      await submitVendorResponse(token, {
        common: {
          companyName: common.companyName.trim() || undefined,
          email: common.email.trim() || undefined,
          sendCopy,
        },
        items: validItems.map<VendorResponseItem>((it) => ({
          eqBrand: it.eqBrand.trim() || undefined,
          eqModel: it.eqModel.trim() || undefined,
          options: it.options.trim() || undefined,
          comment: it.comment.trim() || undefined,
          price: it.price.trim() || undefined,
          askingPrice: it.askingPrice.trim() || undefined,
          qty: it.qty.trim() || undefined,
        })),
      });
      localStorage.setItem(
        VENDOR_DETAILS_KEY,
        JSON.stringify({ companyName: common.companyName.trim(), email: common.email.trim() }),
      );
      setSubmitted(true);
    } catch (error: any) {
      // Surface a reason the vendor can act on, and keep the backend's
      // requestId visible so a support call can be traced straight to the
      // matching entry in the /logs viewer.
      const res = error?.response;
      const serverMsg = res?.data?.message || res?.data?.error;
      const requestId = res?.data?.requestId;

      let text: string;
      if (!res) {
        // No response at all — network failure, CORS, or the API is unreachable.
        text =
          "Could not reach the server. Please check your connection and try again.";
      } else if (res.status === 401) {
        text = "This link is no longer valid. Please use the latest link from your email.";
      } else if (res.status === 410) {
        text = "This link has expired. Please request a new one.";
      } else if (res.status === 400) {
        text = serverMsg || "Some required information is missing or invalid.";
      } else {
        text = serverMsg || "Failed to submit response. Please try again.";
      }

      message.error(requestId ? `${text} (Ref: ${requestId})` : text, 8);

      // Console detail for anyone debugging with devtools open on the vendor's
      // machine — the backend log is the primary record.
      console.error("[vendor-submit] failed", {
        status: res?.status,
        requestId,
        serverMessage: serverMsg,
        error: error?.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Status screens ----
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f0f0f0]">
        <Spin size="large" />
      </div>
    );
  }
  if (tokenError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f0f0f0] p-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow">
          <WarningFilled style={{ fontSize: 44, color: "#b91c1c" }} />
          <h2 className="mt-4 text-lg font-bold text-gray-800">Link Unavailable</h2>
          <p className="mt-2 text-sm text-gray-500">{tokenError}</p>
        </div>
      </div>
    );
  }
  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f0f0f0] p-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-10 text-center shadow">
          <CheckCircleFilled style={{ fontSize: 52, color: "#16a34a" }} />
          <h2 className="mt-4 text-2xl font-bold text-gray-800">Thank you!</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-500">
            Your quote has been submitted and is awaiting review. You may now
            close this window.
          </p>
        </div>
      </div>
    );
  }

  const th: React.CSSProperties = {
    background: "#f8fafc",
    color: "#64748b",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    padding: "12px 14px",
    borderBottom: "1px solid #e2e8f0",
    textAlign: "left",
    whiteSpace: "nowrap",
  };
  const td: React.CSSProperties = {
    padding: "10px 14px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "middle",
  };

  const inputCls = "!h-[38px] !rounded-lg !text-[13px]";

  // WTB issue date shown in the Request Summary.
  const wantListDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const requestId = `WL-${new Date().toISOString().slice(0, 10)}-01`;

  const mailtoHref = `mailto:wantlistreply@alliancetesteq.com?subject=${encodeURIComponent(
    `Re: Want List ${requestId}`,
  )}`;

  return (
    <div className="vf-root min-h-screen" style={{ background: "#f1f4f8" }}>
      <style>{`
        .vf-root {
          font-family: Arial, Helvetica, sans-serif !important;
        }
        .vf-root *,
        .vf-root .ant-input,
        .vf-root .ant-select,
        .vf-root .ant-btn {
          font-family: Arial, Helvetica, sans-serif !important;
        }
        .vf-root .ant-input,
        .vf-root .ant-input-affix-wrapper,
        .vf-root .ant-input-number,
        .vf-root textarea.ant-input,
        .vf-root .ant-select .ant-select-selector {
          border-color: #e2e8f0 !important;
        }
        .vf-root .ant-input:hover,
        .vf-root .ant-input-affix-wrapper:hover,
        .vf-root .ant-select:hover .ant-select-selector {
          border-color: ${BRAND} !important;
        }
        .vf-root .ant-input:focus,
        .vf-root .ant-input-affix-wrapper-focused,
        .vf-root .ant-select-focused .ant-select-selector {
          border-color: ${BRAND} !important;
          box-shadow: 0 0 0 2px rgba(${BRAND_RGB},0.15) !important;
        }
        .vf-root .vf-select.ant-select-single .ant-select-selector {
          height: 34px !important;
          border-radius: 8px !important;
          display: flex !important;
          align-items: center !important;
        }
        .vf-root .vf-select .ant-select-selection-item,
        .vf-root .vf-select .ant-select-selection-placeholder {
          line-height: 32px !important;
          color: #1e293b !important;
        }
        .vf-root .vf-select .ant-select-selection-search-input {
          height: 32px !important;
        }
        .vf-root .vf-select .ant-select-arrow {
          top: 50% !important;
          margin-top: 0 !important;
          transform: translateY(-50%) !important;
        }
      `}</style>

      <div className="mx-auto w-full max-w-6xl px-3 pb-6 pt-2 sm:px-6">

        {/* ============ Top header (no card) ============ */}
        <div className="mb-3 flex flex-col gap-3 px-1 py-1 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <img
              src={require("../../Assets/logo.png")}
              alt="Alliance Test"
              style={{ height: 56, objectFit: "contain", flexShrink: 0 }}
            />
            <div className="hidden h-11 w-px bg-slate-200 sm:block" />
            <div>
              <h1 className="text-[24px] font-extrabold tracking-tight text-slate-800">
                Respond to Want List
              </h1>
              <p className="text-[13px] text-slate-500">
                Submit your pricing and availability for the requested items.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-5 text-[12px] text-slate-500">
            <span className="flex items-start gap-2">
              <SafetyCertificateOutlined style={{ color: BRAND, marginTop: 2 }} />
              <span>
                All submissions are reviewed
                <br className="hidden sm:block" /> before entering our system.
              </span>
            </span>
          </div>
        </div>

        {/* ============ Reply-by-email strip ============ */}
        <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <span className="flex items-center gap-2 text-[13px] text-slate-600">
              <MailOutlined style={{ color: BRAND, fontSize: 16 }} />
              <span><b className="text-slate-800">Prefer email?</b> You can reply directly to this request if you'd like.</span>
            </span>
            <a href={mailtoHref}>
              <Button size="small" icon={<MailOutlined />} style={{ borderColor: BRAND, color: BRAND, height: 30 }}>
                <b>Reply by Email</b>
              </Button>
            </a>
          </div>
          <span className="flex items-center gap-1.5 text-[12px] text-slate-400">
            <ClockCircleOutlined /> Link valid for 60 days.
          </span>
        </div>

        {/* ============ Summary + Details (left card) + How-to-respond (right, full height) ============ */}
        <div className="mb-4 grid grid-cols-1 items-stretch gap-4 lg:grid-cols-3">

          {/* Left card: Request Summary + divider + Your Details */}
          <div className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm lg:col-span-2">

            {/* Request Summary */}
            <div className="p-6">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ background: BRAND }}>
                  <ProfileOutlined style={{ fontSize: 15 }} />
                </span>
                <span className="text-[17px] font-bold text-slate-800">Request Summary</span>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-5 text-[13px] sm:grid-cols-3">
                <div className="space-y-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Want List Date</div>
                    <div className="mt-1 text-slate-700">{wantListDate}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Request ID</div>
                    <div className="mt-1 text-slate-700">{requestId}</div>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Requested By</div>
                  <div className="mt-1 font-bold text-slate-800">Alliance Test Equipment</div>
                </div>
                <div className="space-y-3 text-slate-600">
                  <div className="flex items-center gap-2">
                    <PhoneOutlined style={{ color: BRAND }} /> 508-671-9970
                  </div>
                  <div className="flex items-center gap-2">
                    <MailOutlined style={{ color: BRAND }} />
                    <a href={mailtoHref} style={{ color: BRAND }}>
                      wantlistreply@alliancetesteq.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider between the two sections */}
            <div className="border-t border-slate-100" />

            {/* Your Details */}
            <div className="p-6">
              <div className="mb-1 flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ background: BRAND }}>
                  <UserOutlined style={{ fontSize: 15 }} />
                </span>
                <span className="text-[17px] font-bold text-slate-800">Your Details</span>
                <span className="ml-auto text-[11px] text-slate-400">
                  <span className="text-red-500">*</span> Required
                </span>
              </div>
              <p className="mb-5 ml-12 text-[12px] text-slate-500">Please provide your company contact information.</p>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="relative">
                  <div className="mb-1.5 text-[12px] font-semibold text-slate-600">Company Name <span className="text-red-500">*</span></div>
                  <Input
                    className={inputCls}
                    placeholder="Enter company name"
                    value={common.companyName}
                    onChange={(e) => {
                      setCommon((p) => ({ ...p, companyName: e.target.value }));
                      setCompanySuggestions([]);
                    }}
                    onBlur={checkCompanyNameMatch}
                  />
                  {companySuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
                      <div className="px-3 pt-2 text-[11px] font-semibold text-slate-400">
                        Did you mean:
                      </div>
                      {companySuggestions.map((name) => (
                        <div
                          key={name}
                          className="cursor-pointer px-3 py-2 text-[13px] text-slate-700 hover:bg-slate-50"
                          onMouseDown={(e) => {
                            // onMouseDown (not onClick) fires before the input's
                            // onBlur, so the selection wins instead of being
                            // dismissed by the blur closing the list first.
                            e.preventDefault();
                            setCommon((p) => ({ ...p, companyName: name }));
                            setCompanySuggestions([]);
                          }}
                        >
                          {name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div className="mb-1.5 text-[12px] font-semibold text-slate-600">Email <span className="text-red-500">*</span></div>
                  <Input className={inputCls} type="email" placeholder="Enter your email" value={common.email} onChange={(e) => setCommon((p) => ({ ...p, email: e.target.value }))} />
                </div>
              </div>
            </div>
          </div>

          {/* How to Respond — spans full height of the left card */}
          <div className="flex flex-col rounded-2xl border border-slate-200 p-6 shadow-sm" style={{ background: "#f4f8ff" }}>
            <div className="mb-3 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full text-white" style={{ background: BRAND }}>
                <SendOutlined style={{ fontSize: 15 }} />
              </span>
              <span className="text-[16px] font-bold" style={{ color: BRAND }}>How to Respond</span>
            </div>
            <p className="text-[12px] text-slate-500">You can respond in either of the ways below:</p>
            <div className="mt-4 space-y-4 text-[12px]">
              <div className="flex gap-2.5">
                <ProfileOutlined style={{ color: BRAND, marginTop: 2 }} />
                <div>
                  <div className="font-bold" style={{ color: BRAND }}>Submit Online (Preferred)</div>
                  <div className="text-slate-600">Complete the form below and click <b>Submit Response</b>.</div>
                </div>
              </div>
              <div className="flex gap-2.5">
                <MailOutlined style={{ color: BRAND, marginTop: 2 }} />
                <div>
                  <div className="font-bold" style={{ color: BRAND }}>Reply by Email</div>
                  <div className="text-slate-600">Reply to the original email or click the button above to compose a reply.</div>
                </div>
              </div>
            </div>
            <div className="mt-5 border-t border-slate-200 pt-4 text-[12px] leading-relaxed text-slate-500">
              If you are offering a substitution, please clearly identify the replacement item and include pricing, availability, and calibration status.
            </div>
          </div>
        </div>

        {/* ============ Items to Quote ============ */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

          {/* Section header + info badges */}
          <div className="flex flex-col gap-3 border-b border-slate-100 px-6 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-white" style={{ background: BRAND }}>
                <ShoppingOutlined style={{ fontSize: 15 }} />
              </span>
              <div>
                <div className="text-[17px] font-bold text-slate-800">
                  Items to Quote <span className="font-normal text-slate-400">({items.length})</span>
                </div>
                <div className="text-[12px] text-slate-500">Provide pricing and availability for each item.</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12px] text-slate-600">
                <SwapOutlined style={{ color: BRAND }} /> Substitutions are allowed if clearly identified.
              </span>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 920 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 40, textAlign: "center" }}>#</th>
                  <th style={{ ...th, minWidth: 150 }}>Brand</th>
                  <th style={{ ...th, minWidth: 150 }}>Model</th>
                  <th style={{ ...th, minWidth: 110 }}>Options</th>
                  <th style={{ ...th, width: 70 }}>Qty</th>
                  <th style={{ ...th, minWidth: 100 }}>Price</th>
                  <th style={{ ...th, minWidth: 180, borderRight: "none" }}>Comments</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it.key} style={{ background: idx % 2 === 0 ? "#ffffff" : "#f7f9fc" }}>
                    <td style={{ ...td, textAlign: "center", color: "#64748b", fontWeight: 600 }}>{idx + 1}</td>
                    <td style={{ ...td, fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{it.eqBrand || "—"}</td>
                    <td style={{ ...td, fontSize: 13, color: "#334155" }}>{it.eqModel || "—"}</td>
                    <td style={td}><Input className={inputCls} value={it.options} onChange={(e) => updateItem(it.key, { options: e.target.value })} /></td>
                    <td style={td}><Input className={inputCls} value={it.qty} onChange={(e) => updateItem(it.key, { qty: e.target.value })} /></td>
                    <td style={td}><Input className={inputCls} prefix="$" value={it.price} onChange={(e) => updateItem(it.key, { price: e.target.value })} onBlur={() => updateItem(it.key, { price: formatUsd(it.price) })} /></td>
                    <td style={{ ...td, borderRight: "none" }}><Input className={inputCls} value={it.comment} onChange={(e) => updateItem(it.key, { comment: e.target.value })} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer: confidentiality + actions */}
          <div className="flex flex-col gap-3 border-t border-slate-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between" style={{ background: "#f8fafc" }}>
            <div className="flex flex-col gap-2.5">
              <div className="flex items-start gap-2.5 text-[12px]">
                <SafetyCertificateOutlined style={{ color: BRAND, fontSize: 17, marginTop: 1 }} />
                <div>
                  <div className="font-bold" style={{ color: BRAND }}>Your response is secure and confidential.</div>
                  <div className="text-slate-500">All submissions are reviewed by our team before entering our system.</div>
                  <div className="mt-1 text-slate-400">If our emails are being filtered, please whitelist <b>sanderson@alliancetesteq.com</b> (or the <b>alliancetesteq.com</b> domain).</div>
                </div>
              </div>
              <Checkbox
                checked={sendCopy}
                onChange={(e) => setSendCopy(e.target.checked)}
                className="!text-[12px] !text-slate-600"
              >
                Email me a copy of my submission
              </Checkbox>
            </div>
            <div className="flex gap-3">
              <a href={mailtoHref}>
                <Button icon={<MailOutlined />} style={{ height: 42, borderColor: BRAND, color: BRAND, paddingInline: 18 }}>
                  Reply by Email
                </Button>
              </a>
              <Button
                type="primary"
                loading={submitting}
                onClick={handleSubmit}
                icon={<SendOutlined />}
                style={{ background: BRAND, borderColor: BRAND, height: 42, fontSize: 15, paddingInline: 22 }}
              >
                <b>Submit Response</b>
              </Button>
            </div>
          </div>
        </div>

        {/* ============ Important Notes (mirrors the WTB email) ============ */}
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-3 border-b border-slate-100 px-6 py-4">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-white" style={{ background: BRAND }}>
              <ExclamationCircleOutlined style={{ fontSize: 15 }} />
            </span>
            <div>
              <div className="text-[17px] font-bold text-slate-800">Important Notes</div>
              <div className="text-[12px] text-slate-500">Please review before submitting your response.</div>
            </div>
          </div>

          <div className="px-6 py-5">
            {/* Calibration note */}
            <div className="mb-5 flex items-start gap-3 rounded-xl px-4 py-3" style={{ background: "#e9f2fd" }}>
              <InfoCircleFilled style={{ color: BRAND, fontSize: 16, marginTop: 2 }} />
              <div className="text-[13px] leading-relaxed" style={{ color: "#1f3a5f" }}>
                <b>Please note:</b> If your unit includes calibration, kindly include that in your response.
              </div>
            </div>

            <ul className="space-y-3 text-[13px] leading-relaxed text-slate-600">
              <li className="flex gap-2.5">
                <span style={{ color: BRAND, fontWeight: 700 }}>&bull;</span>
                <span>We are unable to provide target pricing or make offers. We quote our customers based on the most competitive market pricing available to us.</span>
              </li>
              <li className="flex gap-2.5">
                <span style={{ color: BRAND, fontWeight: 700 }}>&bull;</span>
                <span>Vendor pricing is required. If you do not quote a price, we cannot quote or sell your unit. We only respond to vendors who submit pricing.</span>
              </li>
              <li className="flex gap-2.5">
                <span style={{ color: BRAND, fontWeight: 700 }}>&bull;</span>
                <span>Do not offer equipment that has been illegally modified, hacked, firmware-unlocked, license-cloned, serial-number altered, or upgraded through unauthorized means.</span>
              </li>
              <li className="flex gap-2.5">
                <span style={{ color: BRAND, fontWeight: 700 }}>&bull;</span>
                <span>All options, licenses, frequency ranges, bandwidth upgrades, and software features must be factory-installed, OEM-authorized, or fully transferable with proper documentation.</span>
              </li>
              <li className="flex gap-2.5">
                <span style={{ color: BRAND, fontWeight: 700 }}>&bull;</span>
                <span>Any unit found to have unauthorized modifications, altered option data, invalid licenses, counterfeit upgrades, or tampered serial/configuration information will be rejected and may result in removal from our vendor list.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* ============ Contact footer bar ============ */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[12px] text-slate-500">
          <span className="flex items-center gap-1.5"><QuestionCircleOutlined style={{ color: BRAND }} /> Need help?</span>
          <a href="mailto:rkanna@alliancetesteq.com" className="flex items-center gap-1.5" style={{ color: BRAND }}>
            <MailOutlined style={{ color: BRAND }} /> rkanna@alliancetesteq.com
          </a>
          <span className="flex items-center gap-1.5"><PhoneOutlined style={{ color: BRAND }} /> 508-671-9970</span>
          <span>Mon–Fri 8:30 AM – 5:00 PM ET</span>
          <span className="text-slate-300">•</span>
          <span>Alliance Test Equipment, Inc.</span>
          <span className="text-slate-300">•</span>
          <a href="https://www.alliancetesteq.com" target="_blank" rel="noopener noreferrer" style={{ color: BRAND }}>
            www.alliancetesteq.com
          </a>
        </div>
      </div>

      {/* ============ Saved company name confirmation ============ */}
      <Modal
        open={savedNameModal.open}
        onCancel={handleKeepTypedName}
        footer={null}
        centered
        maskClosable={false}
        width={440}
      >
        <div className="pt-1">
          <div className="mb-4 flex items-center gap-3">
            <span
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white"
              style={{ background: BRAND }}
            >
              <UserOutlined style={{ fontSize: 17 }} />
            </span>
            <div>
              <div className="text-[16px] font-bold text-slate-800">
                Just Confirming Your Company Name
              </div>
              <div className="text-[12px] text-slate-500">
                We want to make sure your response is linked to the right account.
              </div>
            </div>
          </div>

          {savedNameModal.matches.length === 1 ? (
            <>
              <p className="mb-3 text-[13px] leading-relaxed text-slate-600">
                You entered <b>"{savedNameModal.typedName}"</b> last time. We found a
                similar company already on file — would you like to use this instead?
              </p>
              <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[13px] font-semibold text-slate-700">
                {savedNameModal.matches[0]}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button onClick={handleKeepTypedName} style={{ height: 38 }}>
                  Keep "{savedNameModal.typedName}"
                </Button>
                <Button
                  type="primary"
                  onClick={() => handleConfirmSavedName(savedNameModal.matches[0])}
                  style={{ background: BRAND, borderColor: BRAND, height: 38 }}
                >
                  Yes, Use This
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="mb-3 text-[13px] leading-relaxed text-slate-600">
                You entered <b>"{savedNameModal.typedName}"</b> last time. We found a
                few similar companies on file — please pick yours if one of these
                looks right, or keep what you already entered.
              </p>
              <div className="mb-4 max-h-52 overflow-y-auto rounded-xl border border-slate-200">
                {savedNameModal.matches.map((name, idx) => (
                  <div
                    key={name}
                    onClick={() => handleConfirmSavedName(name)}
                    className="cursor-pointer px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50"
                    style={{
                      borderBottom:
                        idx === savedNameModal.matches.length - 1 ? "none" : "1px solid #f1f5f9",
                    }}
                  >
                    {name}
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button onClick={handleKeepTypedName} style={{ height: 38 }}>
                  Keep "{savedNameModal.typedName}"
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default VendorQuoteResponse;
